<script setup>
import { computed } from 'vue';
import { Bar, Doughnut, Line } from 'vue-chartjs';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';

const props = defineProps({
  collection: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'bar',
  },
});

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip
);

const palette = [
  '#2684FF',
  '#22C55E',
  '#A855F7',
  '#F59E0B',
  '#14B8A6',
  '#EF4444',
  '#6366F1',
  '#EC4899',
];
const numberFormatter = new Intl.NumberFormat('pt-BR');

const pieLegendLabels = chart => {
  const dataset = chart.data.datasets[0];
  const values = (dataset?.data || []).map(value => Number(value || 0));
  const total = values.reduce((sum, value) => sum + value, 0);

  return (chart.data.labels || []).map((label, index) => {
    const value = values[index] || 0;
    const color = Array.isArray(dataset?.backgroundColor)
      ? dataset.backgroundColor[index]
      : dataset?.backgroundColor;
    const percentage = total ? Math.round((value / total) * 100) : 0;

    return {
      text: `${label}: ${numberFormatter.format(value)} (${percentage}%)`,
      fillStyle: color,
      strokeStyle: color,
      fontColor: '#E4E4E7',
      lineWidth: 0,
      hidden: !chart.getDataVisibility(index),
      index,
    };
  });
};

const component = computed(() => {
  if (props.type === 'line') return Line;
  if (props.type === 'doughnut') return Doughnut;

  return Bar;
});

const chartData = computed(() => ({
  labels: props.collection.labels || [],
  datasets: (props.collection.datasets || []).map((dataset, index) => ({
    ...dataset,
    backgroundColor:
      props.type === 'doughnut'
        ? (dataset.data || []).map(
            (_, colorIndex) => palette[colorIndex % palette.length]
          )
        : dataset.backgroundColor || dataset.background_color || palette[index],
    borderColor:
      dataset.borderColor || dataset.backgroundColor || palette[index],
    borderRadius: props.type === 'bar' ? 8 : 0,
    borderWidth: props.type === 'line' ? 2 : 0,
    fill: props.type === 'line',
    tension: props.type === 'line' ? 0.35 : undefined,
    pointRadius: props.type === 'line' ? 2 : undefined,
  })),
}));

const options = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: props.type === 'doughnut',
      position: 'right',
      labels: {
        color: '#E4E4E7',
        boxWidth: 10,
        padding: 14,
        generateLabels: pieLegendLabels,
      },
    },
  },
  cutout: props.type === 'doughnut' ? '62%' : undefined,
  scales:
    props.type === 'doughnut'
      ? undefined
      : {
          x: { grid: { display: false }, ticks: { color: '#A1A1AA' } },
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(161, 161, 170, 0.1)' },
            ticks: { color: '#A1A1AA' },
          },
        },
}));
</script>

<template>
  <component :is="component" :data="chartData" :options="options" />
</template>
